// Der Wegweiser zur Kapsel — reine Geometrie, ohne Phaser.
//
// Die Kernschleife endet mit „zur Kapsel zurueckkehren" (`GAME-001`). Wer im
// Randfeld ein paar Chunks weit geflogen ist, sieht nur noch Sterne und Nebel:
// die Welt ist endlos und sieht in jeder Richtung gleich aus. Ohne Zeiger ist
// der Rueckweg raten — und Raten ist keine Entscheidung.
//
// Deshalb ein Pfeil am Bildschirmrand, solange die Kapsel ausserhalb des
// Sichtfelds liegt. Die Entfernung steht schon im HUD; der Pfeil traegt sie
// nicht noch einmal.

export interface CompassMark {
  /** Liegt das Ziel im Bild? Dann braucht es keinen Pfeil. */
  onScreen: boolean;
  /** Versatz vom Bildmittelpunkt in Bildpunkten. */
  x: number;
  y: number;
  /** Richtung zum Ziel im Bogenmass. */
  angle: number;
  /** Entfernung in Welteinheiten. */
  dist: number;
}

/**
 * Wo der Zeiger auf das Ziel steht.
 *
 * `halfW`/`halfH` sind die halben Kantenlaengen des Sichtfelds in
 * Bildpunkten, `margin` der Abstand, den der Pfeil zum Rand haelt. Liegt das
 * Ziel ausserhalb, wird der Punkt auf dem Strahl an den Rand des Rechtecks
 * geklemmt — so wandert der Pfeil um den Bildrand, statt zu springen.
 */
export function compassMark(
  centerX: number,
  centerY: number,
  targetX: number,
  targetY: number,
  zoom: number,
  halfW: number,
  halfH: number,
  margin: number,
): CompassMark {
  const dx = targetX - centerX;
  const dy = targetY - centerY;
  const angle = Math.atan2(dy, dx);
  const dist = Math.hypot(dx, dy);

  let sx = dx * zoom;
  let sy = dy * zoom;
  const limitX = Math.max(0, halfW - margin);
  const limitY = Math.max(0, halfH - margin);

  if (Math.abs(sx) <= limitX && Math.abs(sy) <= limitY) {
    return { onScreen: true, x: sx, y: sy, angle, dist };
  }

  // Auf den Rand des erlaubten Rechtecks skalieren. Der laengere Anteil
  // bestimmt, an welcher Kante der Pfeil liegt.
  const scale = Math.min(
    Math.abs(sx) > 0.0001 ? limitX / Math.abs(sx) : Number.POSITIVE_INFINITY,
    Math.abs(sy) > 0.0001 ? limitY / Math.abs(sy) : Number.POSITIVE_INFINITY,
  );
  if (Number.isFinite(scale)) { sx *= scale; sy *= scale; }

  return { onScreen: false, x: sx, y: sy, angle, dist };
}
